/**
 * AI Brain - main AI decision-making system using behavior trees
 */

import type { World } from '@/core/ecs/World';
import { ComponentTypes } from '@/core/ecs/Component';
import type { PositionComponent } from '@/game/components/Position';
import type { FactionComponent } from '@/game/components/Faction';
import type { BuildingComponent } from '@/game/components/Building';
import type { SpawnerComponent } from '@/game/components/Spawner';
import type { HealthComponent } from '@/game/components/Health';
import type { UnitComponent } from '@/game/components/Unit';
import type { FactionManager, FactionState } from '@/game/factions/FactionManager';
import type { TerritorySystem } from '@/game/systems/TerritorySystem';
import { createBuildingEntity } from '@/game/entities/createBuilding';
import { BUILDING_STATS } from '@/game/factions/FactionData';
import { tileHeight, isLand, getSurfaceY } from '@/rendering/terrain/TerrainGenerator';
import { tileOccupancy } from '@/game/systems/TileOccupancy';
import type { FactionId, BuildingType } from '@/types';
import {
  type BehaviorNode,
  type BehaviorContext,
  Selector,
  Sequence,
  Condition,
  Action,
  Cooldown,
} from './BehaviorTree';
import {
  FACTION_PERSONALITIES,
  type AIPersonality,
  type AIStrategy,
  determineStrategy,
  getBuildingPriorities,
} from './FactionPersonality';
import {
  type DifficultyLevel,
  type DifficultyModifiers,
  getDifficultyModifiers,
} from './DifficultySettings';
import { useGameStore } from '@/state/gameStore';

export interface AIContext extends BehaviorContext {
  factionId: FactionId;
  personality: AIPersonality;
  strategy: AIStrategy;
  factionState: FactionState;
  threatLevel: number;
  economicStrength: number;
  militaryStrength: number;
  gameTime: number;
  world: World;
  factionManager: FactionManager;
  territorySystem: TerritorySystem;
  difficulty: DifficultyModifiers;
}

export class AIBrain {
  private world: World;
  private factionManager: FactionManager;
  private territorySystem: TerritorySystem;
  private factionId: FactionId;
  private personality: AIPersonality;
  private behaviorTree: BehaviorNode<AIContext>;
  private gameTime: number = 0;

  // Cooldowns
  private lastStrategicUpdate: number = 0;
  private lastTacticalUpdate: number = 0;
  private lastOperationalUpdate: number = 0;

  // Cached state
  private strategy: AIStrategy = 'balanced';
  private threatLevel: number = 0;
  private economicStrength: number = 0.5;
  private militaryStrength: number = 0.5;

  // Difficulty
  private getDifficulty(): DifficultyModifiers {
    const level = useGameStore.getState().difficulty;
    return getDifficultyModifiers(level);
  }

  constructor(
    world: World,
    factionManager: FactionManager,
    territorySystem: TerritorySystem,
    factionId: FactionId
  ) {
    this.world = world;
    this.factionManager = factionManager;
    this.territorySystem = territorySystem;
    this.factionId = factionId;
    this.personality = FACTION_PERSONALITIES[factionId];
    this.behaviorTree = this.createBehaviorTree();
  }

  private createBehaviorTree(): BehaviorNode<AIContext> {
    const getTime = () => this.gameTime;
    const getStrategicInterval = () => this.getDifficulty().strategicUpdateInterval;
    const getTacticalInterval = () => this.getDifficulty().tacticalUpdateInterval;

    return new Selector<AIContext>([
      // Strategic layer - interval based on difficulty
      new Cooldown(
        new Sequence<AIContext>([
          new Action((ctx) => this.assessThreat(ctx)),
          new Action((ctx) => this.updateStrategy(ctx)),
        ]),
        getStrategicInterval(),
        getTime
      ),

      // Tactical layer - interval based on difficulty
      new Cooldown(
        new Selector<AIContext>([
          // Emergency response - defend if under attack
          new Sequence([
            new Condition((ctx) => ctx.threatLevel > 0.6),
            new Action((ctx) => this.emergencyDefense(ctx)),
          ]),

          // Economic management
          new Sequence([
            new Condition((ctx) => ctx.strategy === 'economic' || ctx.factionState.gold > 400),
            new Action((ctx) => this.manageEconomy(ctx)),
          ]),

          // Military management
          new Sequence([
            new Condition((ctx) => ctx.strategy !== 'economic'),
            new Action((ctx) => this.manageMilitary(ctx)),
          ]),

          // Default building
          new Action((ctx) => this.buildSomething(ctx)),
        ]),
        getTacticalInterval(),
        getTime
      ),

      // Operational layer - runs every 1 second
      new Cooldown(
        new Selector<AIContext>([
          // Update rally points
          new Action((ctx) => this.updateRallyPoints(ctx)),
        ]),
        1,
        getTime
      ),
    ]);
  }

  update(deltaTime: number): void {
    this.gameTime += deltaTime;

    const factionState = this.factionManager.getFactionState(this.factionId);
    if (!factionState || factionState.isDefeated || factionState.isPlayer) return;

    const difficulty = this.getDifficulty();

    const context: AIContext = {
      factionId: this.factionId,
      personality: this.personality,
      strategy: this.strategy,
      factionState,
      threatLevel: this.threatLevel,
      economicStrength: this.economicStrength,
      militaryStrength: this.militaryStrength,
      gameTime: this.gameTime,
      world: this.world,
      factionManager: this.factionManager,
      territorySystem: this.territorySystem,
      difficulty,
    };

    this.behaviorTree.tick(context);
  }

  private assessThreat(ctx: AIContext): 'success' | 'failure' {
    // Count our units and buildings
    const ourUnits = this.countUnits(ctx.factionId);
    const ourBuildings = this.countAllBuildings(ctx.factionId);

    // Count enemy units nearby
    let nearbyEnemyUnits = 0;
    const ourCenter = this.getAveragePosition(ctx.factionId);

    if (ourCenter) {
      const entities = ctx.world.query([ComponentTypes.Position, ComponentTypes.Faction, ComponentTypes.Unit]);
      for (const entity of entities) {
        const pos = ctx.world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
        const faction = ctx.world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;

        if (ctx.factionManager.isEnemy(ctx.factionId, faction.factionId)) {
          const dx = pos.x - ourCenter.x;
          const dz = pos.z - ourCenter.z;
          const dist = Math.sqrt(dx * dx + dz * dz);
          if (dist < 40) nearbyEnemyUnits++;
        }
      }
    }

    // Calculate threat level
    this.threatLevel = Math.min(1, nearbyEnemyUnits / Math.max(1, ourUnits) * 0.5);

    // Calculate economic and military strength relative to average
    const activeFactions = ctx.factionManager.getActiveFactions();
    let totalUnits = 0;
    let totalGold = 0;

    for (const fid of activeFactions) {
      const state = ctx.factionManager.getFactionState(fid);
      if (state) {
        totalUnits += state.unitCount;
        totalGold += state.gold;
      }
    }

    const avgUnits = totalUnits / activeFactions.length;
    const avgGold = totalGold / activeFactions.length;

    this.militaryStrength = Math.min(1, ourUnits / Math.max(1, avgUnits));
    this.economicStrength = Math.min(1, ctx.factionState.gold / Math.max(1, avgGold));

    return 'success';
  }

  private updateStrategy(ctx: AIContext): 'success' | 'failure' {
    this.strategy = determineStrategy(
      ctx.personality,
      this.threatLevel,
      this.economicStrength,
      this.militaryStrength
    );
    return 'success';
  }

  private getAdjustedCost(baseCost: number, ctx: AIContext): number {
    return Math.floor(baseCost * ctx.difficulty.buildingCostMultiplier);
  }

  private emergencyDefense(ctx: AIContext): 'success' | 'failure' {
    const towerCost = this.getAdjustedCost(BUILDING_STATS.tower.cost, ctx);
    // Build towers if we can afford them
    if (ctx.factionState.gold >= towerCost) {
      const pos = this.findDefensivePosition(ctx.factionId, 'tower');
      if (pos) {
        ctx.factionManager.spendGold(ctx.factionId, towerCost);
        const entity = createBuildingEntity(ctx.world, 'tower', ctx.factionId, pos.x, pos.y, pos.z);
        tileOccupancy.occupyTiles(pos.x, pos.z, 'tower', entity);
        return 'success';
      }
    }

    const smithyCost = this.getAdjustedCost(BUILDING_STATS.smithy.cost, ctx);
    // Build shields for defense
    if (ctx.factionState.gold >= smithyCost) {
      const smithyCount = this.countBuildings(ctx.factionId, 'smithy');
      if (smithyCount < 2) {
        const pos = this.findBuildPosition(ctx.factionId, 'smithy');
        if (pos) {
          ctx.factionManager.spendGold(ctx.factionId, smithyCost);
          const entity = createBuildingEntity(ctx.world, 'smithy', ctx.factionId, pos.x, pos.y, pos.z);
          tileOccupancy.occupyTiles(pos.x, pos.z, 'smithy', entity);
          return 'success';
        }
      }
    }

    return 'failure';
  }

  private manageEconomy(ctx: AIContext): 'success' | 'failure' {
    // Economic buildings with faction-specific priorities
    const priorities = getBuildingPriorities(ctx.strategy, ctx.personality);

    const economicBuildings: { type: BuildingType; maxCount: number }[] = [
      { type: 'farm', maxCount: 4 },
      { type: 'mine', maxCount: 3 },
      { type: 'market', maxCount: 2 },
    ];

    const options = economicBuildings.map(b => ({
      type: b.type,
      priority: priorities[b.type] || 0.5,
      count: this.countBuildings(ctx.factionId, b.type),
      maxCount: b.maxCount,
      cost: this.getAdjustedCost(BUILDING_STATS[b.type].cost, ctx),
    }));

    // Sort by effective priority (priority / (count + 1)) and filter affordable options
    const validOptions = options
      .filter(o => o.count < o.maxCount && ctx.factionState.gold >= o.cost)
      .sort((a, b) => (b.priority / (b.count + 1)) - (a.priority / (a.count + 1)));

    if (validOptions.length > 0) {
      const chosen = validOptions[0];
      const pos = this.findBuildPosition(ctx.factionId, chosen.type);
      if (pos) {
        ctx.factionManager.spendGold(ctx.factionId, chosen.cost);
        const entity = createBuildingEntity(ctx.world, chosen.type, ctx.factionId, pos.x, pos.y, pos.z);
        tileOccupancy.occupyTiles(pos.x, pos.z, chosen.type, entity);
        return 'success';
      }
    }

    return 'failure';
  }

  private manageMilitary(ctx: AIContext): 'success' | 'failure' {
    // All military/production buildings with faction-specific priorities
    const priorities = getBuildingPriorities(ctx.strategy, ctx.personality);

    // All military/production buildings (excluding capital, farm, mine, market)
    const militaryBuildings: { type: BuildingType; maxCount: number }[] = [
      { type: 'barracks', maxCount: 4 },    // Sword units
      { type: 'archery', maxCount: 3 },     // Archer units
      { type: 'spearcamp', maxCount: 3 },   // Spear units
      { type: 'stable', maxCount: 3 },      // Cavalry units
      { type: 'smithy', maxCount: 2 },      // Shield units
      { type: 'temple', maxCount: 2 },      // Taoist units
      { type: 'tower', maxCount: 4 },       // Defense tower
      { type: 'wall', maxCount: 20 },       // Defense walls
    ];

    const options = militaryBuildings.map(b => ({
      type: b.type,
      priority: priorities[b.type] || 0.5,
      count: this.countBuildings(ctx.factionId, b.type),
      maxCount: b.maxCount,
      cost: this.getAdjustedCost(BUILDING_STATS[b.type].cost, ctx),
    }));

    // Sort by effective priority (priority / (count + 1)) and filter affordable options
    const validOptions = options
      .filter(o => o.count < o.maxCount && ctx.factionState.gold >= o.cost)
      .sort((a, b) => (b.priority / (b.count + 1)) - (a.priority / (a.count + 1)));

    if (validOptions.length > 0) {
      const chosen = validOptions[0];
      const pos = this.findBuildPosition(ctx.factionId, chosen.type);
      if (pos) {
        ctx.factionManager.spendGold(ctx.factionId, chosen.cost);
        const entity = createBuildingEntity(ctx.world, chosen.type, ctx.factionId, pos.x, pos.y, pos.z);
        tileOccupancy.occupyTiles(pos.x, pos.z, chosen.type, entity);
        return 'success';
      }
    }

    return 'failure';
  }

  private buildSomething(ctx: AIContext): 'success' | 'failure' {
    // Unified building decision using all 10 building types
    const priorities = getBuildingPriorities(ctx.strategy, ctx.personality);

    // All buildable types with max counts
    const allBuildings: { type: BuildingType; maxCount: number }[] = [
      { type: 'barracks', maxCount: 4 },
      { type: 'archery', maxCount: 3 },
      { type: 'spearcamp', maxCount: 3 },
      { type: 'stable', maxCount: 3 },
      { type: 'smithy', maxCount: 2 },
      { type: 'temple', maxCount: 2 },
      { type: 'tower', maxCount: 4 },
      { type: 'wall', maxCount: 20 },
      { type: 'farm', maxCount: 4 },
      { type: 'mine', maxCount: 3 },
      { type: 'market', maxCount: 2 },
    ];

    const options = allBuildings.map(b => ({
      type: b.type,
      priority: priorities[b.type] || 0.5,
      count: this.countBuildings(ctx.factionId, b.type),
      maxCount: b.maxCount,
      cost: this.getAdjustedCost(BUILDING_STATS[b.type].cost, ctx),
    }));

    // Sort by effective priority and filter affordable options
    const validOptions = options
      .filter(o => o.count < o.maxCount && ctx.factionState.gold >= o.cost)
      .sort((a, b) => (b.priority / (b.count + 1)) - (a.priority / (a.count + 1)));

    if (validOptions.length > 0) {
      const chosen = validOptions[0];
      const pos = this.findBuildPosition(ctx.factionId, chosen.type);
      if (pos) {
        ctx.factionManager.spendGold(ctx.factionId, chosen.cost);
        const entity = createBuildingEntity(ctx.world, chosen.type, ctx.factionId, pos.x, pos.y, pos.z);
        tileOccupancy.occupyTiles(pos.x, pos.z, chosen.type, entity);
        return 'success';
      }
    }

    return 'failure';
  }

  private updateRallyPoints(ctx: AIContext): 'success' | 'failure' {
    const enemyTarget = this.findEnemyTarget(ctx.factionId);
    if (!enemyTarget) return 'failure';

    const spawners = ctx.world.query([ComponentTypes.Spawner, ComponentTypes.Faction, ComponentTypes.Position]);

    for (const entity of spawners) {
      const faction = ctx.world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;
      if (faction.factionId !== ctx.factionId) continue;

      const spawner = ctx.world.getComponent<SpawnerComponent>(entity, ComponentTypes.Spawner)!;
      const position = ctx.world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;

      const dx = enemyTarget.x - position.x;
      const dz = enemyTarget.z - position.z;
      const dist = Math.sqrt(dx * dx + dz * dz);

      if (dist > 5) {
        // More aggressive rally points based on personality and difficulty
        const effectiveAggression = Math.min(1, ctx.personality.aggression + ctx.difficulty.aggressionBoost);
        const rallyDist = Math.min(dist * (0.3 + effectiveAggression * 0.5), 30);
        spawner.rallyX = position.x + (dx / dist) * rallyDist;
        spawner.rallyZ = position.z + (dz / dist) * rallyDist;
      }
    }

    return 'success';
  }

  // Helper methods
  private countBuildings(factionId: FactionId, buildingType: string): number {
    const buildings = this.world.query([ComponentTypes.Building, ComponentTypes.Faction]);
    let count = 0;

    for (const entity of buildings) {
      const building = this.world.getComponent<BuildingComponent>(entity, ComponentTypes.Building)!;
      const faction = this.world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;

      if (faction.factionId === factionId && building.buildingType === buildingType) {
        count++;
      }
    }

    return count;
  }

  private countAllBuildings(factionId: FactionId): number {
    const buildings = this.world.query([ComponentTypes.Building, ComponentTypes.Faction]);
    let count = 0;

    for (const entity of buildings) {
      const faction = this.world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;
      if (faction.factionId === factionId) count++;
    }

    return count;
  }

  private countUnits(factionId: FactionId): number {
    const units = this.world.query([ComponentTypes.Unit, ComponentTypes.Faction]);
    let count = 0;

    for (const entity of units) {
      const faction = this.world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;
      if (faction.factionId === factionId) count++;
    }

    return count;
  }

  private getAveragePosition(factionId: FactionId): { x: number; z: number } | null {
    const buildings = this.world.query([ComponentTypes.Building, ComponentTypes.Faction, ComponentTypes.Position]);

    let x = 0, z = 0, count = 0;

    for (const entity of buildings) {
      const faction = this.world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;
      if (faction.factionId !== factionId) continue;

      const pos = this.world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
      x += pos.x;
      z += pos.z;
      count++;
    }

    if (count === 0) return null;
    return { x: x / count, z: z / count };
  }

  private findBuildPosition(factionId: FactionId, buildingType: BuildingType = 'barracks'): { x: number; y: number; z: number } | null {
    const center = this.getAveragePosition(factionId);
    if (!center) return null;

    for (let attempt = 0; attempt < 30; attempt++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = 3 + Math.random() * 12;
      const x = center.x + Math.cos(angle) * dist;
      const z = center.z + Math.sin(angle) * dist;

      const h = tileHeight(x, z);
      if (!isLand(h)) continue;

      // Check if within AI's own territory
      const owner = this.territorySystem.getTileOwner(x, z);
      if (owner !== factionId) continue;

      // Check if tiles are not occupied by other buildings
      const stats = BUILDING_STATS[buildingType];
      const tiles = tileOccupancy.getTilesForBuilding(x, z, stats.size);
      let allValid = true;

      for (const tile of tiles) {
        const tileX = tile.x + 0.5;
        const tileZ = tile.z + 0.5;

        // Check land
        if (!isLand(tileHeight(tileX, tileZ))) {
          allValid = false;
          break;
        }

        // Check occupancy
        if (tileOccupancy.isOccupied(tile.x, tile.z)) {
          allValid = false;
          break;
        }

        // Check territory
        if (this.territorySystem.getTileOwner(tileX, tileZ) !== factionId) {
          allValid = false;
          break;
        }
      }

      if (allValid) {
        return { x, y: getSurfaceY(x, z), z };
      }
    }

    return null;
  }

  private findDefensivePosition(factionId: FactionId, buildingType: BuildingType = 'tower'): { x: number; y: number; z: number } | null {
    const center = this.getAveragePosition(factionId);
    if (!center) return null;

    // Find enemy direction
    const enemyTarget = this.findEnemyTarget(factionId);
    if (!enemyTarget) return this.findBuildPosition(factionId, buildingType);

    const dx = enemyTarget.x - center.x;
    const dz = enemyTarget.z - center.z;
    const dist = Math.sqrt(dx * dx + dz * dz);

    if (dist < 1) return this.findBuildPosition(factionId, buildingType);

    // Build toward enemy direction but within territory
    for (let attempt = 0; attempt < 30; attempt++) {
      const angleOffset = (Math.random() - 0.5) * Math.PI * 0.5;
      const angle = Math.atan2(dz, dx) + angleOffset;
      const buildDist = 5 + Math.random() * 8;

      const x = center.x + Math.cos(angle) * buildDist;
      const z = center.z + Math.sin(angle) * buildDist;

      const h = tileHeight(x, z);
      if (!isLand(h)) continue;

      // Check if within AI's own territory
      const owner = this.territorySystem.getTileOwner(x, z);
      if (owner !== factionId) continue;

      // Check if tiles are not occupied
      const stats = BUILDING_STATS[buildingType];
      const tiles = tileOccupancy.getTilesForBuilding(x, z, stats.size);
      let allValid = true;

      for (const tile of tiles) {
        const tileX = tile.x + 0.5;
        const tileZ = tile.z + 0.5;

        if (!isLand(tileHeight(tileX, tileZ))) {
          allValid = false;
          break;
        }

        if (tileOccupancy.isOccupied(tile.x, tile.z)) {
          allValid = false;
          break;
        }

        if (this.territorySystem.getTileOwner(tileX, tileZ) !== factionId) {
          allValid = false;
          break;
        }
      }

      if (allValid) {
        return { x, y: getSurfaceY(x, z), z };
      }
    }

    return null;
  }

  private findEnemyTarget(factionId: FactionId): { x: number; z: number } | null {
    const buildings = this.world.query([ComponentTypes.Building, ComponentTypes.Faction, ComponentTypes.Position]);
    const center = this.getAveragePosition(factionId);
    if (!center) return null;

    let nearestDist = Infinity;
    let nearestTarget: { x: number; z: number } | null = null;

    for (const entity of buildings) {
      const faction = this.world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;
      if (faction.factionId === factionId) continue;
      if (!this.factionManager.isEnemy(factionId, faction.factionId)) continue;

      const building = this.world.getComponent<BuildingComponent>(entity, ComponentTypes.Building)!;
      const position = this.world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;

      const dx = position.x - center.x;
      const dz = position.z - center.z;
      let dist = Math.sqrt(dx * dx + dz * dz);

      // Prioritize capitals
      if (building.buildingType === 'capital') {
        dist *= 0.5;
      }

      if (dist < nearestDist) {
        nearestDist = dist;
        nearestTarget = { x: position.x, z: position.z };
      }
    }

    return nearestTarget;
  }
}
